/**
 * Labels for the document-import card (ImportBatchCard): chips, the count
 * strip, notices, the import button, the student invitation warning, problem
 * sentences and the aria labels — in each card language.
 *
 * A sibling of `card-labels.ts` (same `Language`, same pattern: one table per
 * language, English included). Field captions are NOT here: columns and
 * problem fields are captioned by `card-labels.ts`'s `fieldCaption`.
 *
 * Counts go through `Intl.PluralRules`: Arabic has zero/one/two/few/many/
 * other ("استيراد عميل محتمل واحد", "استيراد 3 عملاء محتملين", "استيراد 11
 * عميلًا محتملًا"); English, Spanish and French have one/other (a newer
 * CLDR "many" for millions falls back to "other"). Spanish is formal
 * ("usted"), as on the rest of the card.
 *
 * Problem `detail` is a VALUE (a raw phone, a date, an ISO country, a row
 * number) — it is interpolated as-is, never translated. An unknown code reads
 * "Needs attention" with the field caption, never the raw code.
 */
import { fieldCaption, type Language } from './card-labels';

export const IMPORT_RECORD_TYPES = [
  'lead',
  'contact',
  'task',
  'product',
  'student',
] as const;

export type ImportRecordType = (typeof IMPORT_RECORD_TYPES)[number];

interface ImportStaticLabels {
  chipReady: string;
  chipWarning: string;
  chipDuplicateExisting: string;
  chipInvalid: string;
  chipOverPlanLimit: string;
  countReady: string;
  countWarning: string;
  countDuplicates: string;
  countInvalid: string;
  countOverPlanLimit: string;
  showProblemsOnly: string;
  selectAllReady: string;
  selectPage: string;
  columnRow: string;
  columnPage: string;
  columnStatus: string;
  appliedToAll: string;
  instructions: string;
  previousPage: string;
  nextPage: string;
}

export const IMPORT_LABELS: Record<Language, ImportStaticLabels> = {
  en: {
    chipReady: 'Ready',
    chipWarning: 'Warning',
    chipDuplicateExisting: 'Already in the CRM',
    chipInvalid: 'Needs fixing',
    chipOverPlanLimit: 'Plan limit',
    countReady: 'Ready',
    countWarning: 'Warnings',
    countDuplicates: 'Duplicates',
    countInvalid: 'Need fixing',
    countOverPlanLimit: 'Over plan limit',
    showProblemsOnly: 'Show problems only',
    selectAllReady: 'Select all ready',
    selectPage: 'Select the rows on this page',
    columnRow: 'Row',
    columnPage: 'Page',
    columnStatus: 'Status',
    appliedToAll: 'Applied to all rows',
    instructions: 'Your instructions',
    previousPage: 'Previous',
    nextPage: 'Next',
  },
  ar: {
    chipReady: 'جاهز',
    chipWarning: 'تنبيه',
    chipDuplicateExisting: 'موجود مسبقًا',
    chipInvalid: 'يحتاج إلى تصحيح',
    chipOverPlanLimit: 'حد الخطة',
    countReady: 'جاهزة',
    countWarning: 'تنبيهات',
    countDuplicates: 'مكررة',
    countInvalid: 'تحتاج إلى تصحيح',
    countOverPlanLimit: 'تتجاوز حد الخطة',
    showProblemsOnly: 'إظهار المشكلات فقط',
    selectAllReady: 'تحديد كل الجاهزة',
    selectPage: 'تحديد صفوف هذه الصفحة',
    columnRow: 'الصف',
    columnPage: 'الصفحة',
    columnStatus: 'الحالة',
    appliedToAll: 'يُطبَّق على كل الصفوف',
    instructions: 'تعليماتك',
    previousPage: 'السابق',
    nextPage: 'التالي',
  },
  es: {
    // Agrees with "fila" (feminine), like the count strip's "Listas".
    chipReady: 'Lista',
    chipWarning: 'Advertencia',
    chipDuplicateExisting: 'Ya en el CRM',
    chipInvalid: 'Requiere corrección',
    chipOverPlanLimit: 'Límite del plan',
    countReady: 'Listas',
    countWarning: 'Advertencias',
    countDuplicates: 'Duplicados',
    countInvalid: 'Por corregir',
    countOverPlanLimit: 'Exceden el límite del plan',
    showProblemsOnly: 'Mostrar solo los problemas',
    selectAllReady: 'Seleccionar todas las filas listas',
    selectPage: 'Seleccionar las filas de esta página',
    columnRow: 'Fila',
    columnPage: 'Página',
    columnStatus: 'Estado',
    appliedToAll: 'Se aplica a todas las filas',
    instructions: 'Sus instrucciones',
    previousPage: 'Anterior',
    nextPage: 'Siguiente',
  },
  fr: {
    // Agrees with "ligne" (feminine), like the count strip's "Prêtes".
    chipReady: 'Prête',
    chipWarning: 'Avertissement',
    chipDuplicateExisting: 'Déjà dans le CRM',
    chipInvalid: 'À corriger',
    chipOverPlanLimit: 'Limite du forfait',
    countReady: 'Prêtes',
    countWarning: 'Avertissements',
    countDuplicates: 'Doublons',
    countInvalid: 'Lignes à corriger',
    countOverPlanLimit: 'Au-delà du forfait',
    showProblemsOnly: 'Afficher uniquement les problèmes',
    selectAllReady: 'Sélectionner toutes les lignes prêtes',
    selectPage: 'Sélectionner les lignes de cette page',
    columnRow: 'Ligne',
    // A true cognate: the French word is "Page".
    columnPage: 'Page',
    columnStatus: 'Statut',
    appliedToAll: 'Appliqué à toutes les lignes',
    instructions: 'Vos instructions',
    previousPage: 'Précédent',
    nextPage: 'Suivant',
  },
};

/**
 * A default's field caption for the "Applied to all rows" line. A
 * default_fields key (collab/import/bulk_create_v1.json contract) ending in
 * `_id`/`_ids` (assignee_id, tag_ids, program_ids, …) names an id column,
 * but the default's `label` shown beside it is always a NAME, not an id —
 * so the line captions the noun instead (assignee, tags, programs), never
 * the raw id key. Every other field keeps its own caption, unchanged.
 */
export function defaultFieldCaption(language: Language, field: string): string {
  const noun = field.replace(/_id(s?)$/, '$1');
  return fieldCaption(language, noun);
}

export function importLabel(
  language: Language,
  key: keyof ImportStaticLabels
): string {
  return IMPORT_LABELS[language][key];
}

/** Plural forms keyed by `Intl.PluralRules` category; `other` is required. */
type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & {
  other: string;
};

function plural(language: Language, n: number, forms: PluralForms): string {
  const category = new Intl.PluralRules(language).select(n);
  return (forms[category] ?? forms.other).split('{n}').join(String(n));
}

/** "{n} leads" per record type and language ({n} is the count). */
const NOUNS: Record<Language, Record<ImportRecordType, PluralForms>> = {
  en: {
    lead: { one: '{n} lead', other: '{n} leads' },
    contact: { one: '{n} contact', other: '{n} contacts' },
    task: { one: '{n} task', other: '{n} tasks' },
    product: { one: '{n} product', other: '{n} products' },
    student: { one: '{n} student', other: '{n} students' },
  },
  ar: {
    lead: {
      zero: '{n} عميل محتمل',
      one: 'عميل محتمل واحد',
      two: 'عميلين محتملين',
      few: '{n} عملاء محتملين',
      many: '{n} عميلًا محتملًا',
      other: '{n} عميل محتمل',
    },
    contact: {
      zero: '{n} جهة اتصال',
      one: 'جهة اتصال واحدة',
      two: 'جهتي اتصال',
      few: '{n} جهات اتصال',
      many: '{n} جهةَ اتصال',
      other: '{n} جهة اتصال',
    },
    task: {
      zero: '{n} مهمة',
      one: 'مهمة واحدة',
      two: 'مهمتين',
      few: '{n} مهام',
      many: '{n} مهمةً',
      other: '{n} مهمة',
    },
    product: {
      zero: '{n} منتج',
      one: 'منتج واحد',
      two: 'منتجين',
      few: '{n} منتجات',
      many: '{n} منتجًا',
      other: '{n} منتج',
    },
    student: {
      zero: '{n} طالب',
      one: 'طالب واحد',
      two: 'طالبين',
      few: '{n} طلاب',
      many: '{n} طالبًا',
      other: '{n} طالب',
    },
  },
  es: {
    lead: { one: '{n} cliente potencial', other: '{n} clientes potenciales' },
    contact: { one: '{n} contacto', other: '{n} contactos' },
    task: { one: '{n} tarea', other: '{n} tareas' },
    product: { one: '{n} producto', other: '{n} productos' },
    student: { one: '{n} estudiante', other: '{n} estudiantes' },
  },
  fr: {
    lead: { one: '{n} prospect', other: '{n} prospects' },
    contact: { one: '{n} contact', other: '{n} contacts' },
    task: { one: '{n} tâche', other: '{n} tâches' },
    product: { one: '{n} produit', other: '{n} produits' },
    student: { one: '{n} étudiant', other: '{n} étudiants' },
  },
};

function nounForms(language: Language, recordType: string): PluralForms {
  const table = NOUNS[language];
  return (table as Record<string, PluralForms>)[recordType] ?? table.lead;
}

/** "5 leads" / "عميل محتمل واحد" — the count with its noun. */
export function countNoun(
  language: Language,
  recordType: string,
  n: number
): string {
  return plural(language, n, nounForms(language, recordType));
}

const IMPORT_VERB: Record<Language, string> = {
  en: 'Import',
  ar: 'استيراد',
  es: 'Importar',
  fr: 'Importer',
};

/** The primary button: "Import 3 leads". */
export function importButtonLabel(
  language: Language,
  recordType: string,
  n: number
): string {
  return `${IMPORT_VERB[language]} ${countNoun(language, recordType, n)}`;
}

/**
 * The preposition before the import card's source filename ("from
 * leads.pdf"). Not the Arabic preposition "من" used for a destination or
 * cause — this one specifically introduces a source document.
 */
const TITLE_FROM: Record<Language, string> = {
  en: 'from',
  ar: 'من',
  es: 'desde',
  fr: 'depuis',
};

/**
 * The joiner between the importable count and the total, when they differ:
 * "Import 8 **of** 12 leads". Arabic reads "من أصل" ("out of a total of"),
 * not the "من" used for a source document (TITLE_FROM) or a destination.
 */
const TITLE_OF: Record<Language, string> = {
  en: 'of',
  ar: 'من أصل',
  es: 'de',
  fr: 'sur',
};

/**
 * A record type's plural noun for `n`, on its own — no leading count — plus
 * whether that template already states the count itself. Most `NOUNS`
 * templates are "{n} noun" (English, Spanish, French, and three of
 * Arabic's six categories: zero, few, many, other), so the digit belongs
 * before the bare word once stripped of "{n}". Arabic's "one" and "two"
 * categories are already fixed phrases that name the count on their own —
 * "عميل محتمل واحد" already says "one", "عميلين محتملين" is already dual,
 * for every record type (طالبين، مهمتين، جهتي اتصال …) — so `statesOwnCount`
 * tells the caller never to write that digit a second time. Used for the
 * "of total" title, where the total would otherwise be written once before
 * "من أصل"/"of"/"de"/"sur" and the noun, each already carrying its own.
 */
function bareNoun(
  language: Language,
  recordType: string,
  n: number
): { noun: string; statesOwnCount: boolean } {
  const forms = nounForms(language, recordType);
  const category = new Intl.PluralRules(language).select(n);
  const template = forms[category] ?? forms.other;
  return {
    noun: template.replace('{n}', '').trim(),
    statesOwnCount: !template.includes('{n}'),
  };
}

/**
 * "Import {importable} of {total} {noun}", the noun agreeing with total.
 * The total's digit is dropped when the noun's own form already states the
 * count (Arabic's "one"/"two" categories) — never written twice.
 */
function partialImportLead(
  language: Language,
  recordType: string,
  importable: number,
  total: number
): string {
  const { noun, statesOwnCount } = bareNoun(language, recordType, total);
  const totalPhrase = statesOwnCount ? noun : `${total} ${noun}`;
  return `${IMPORT_VERB[language]} ${importable} ${TITLE_OF[language]} ${totalPhrase}`;
}

/**
 * The record type's gender in Spanish and French, for the "nothing
 * importable" title's determiner (ningún/ninguna, aucun/aucune) and, in
 * French, the agreeing past participle (importé/importée).
 */
const NEGATIVE_GENDER: Record<
  ImportRecordType,
  { es: 'm' | 'f'; fr: 'm' | 'f' }
> = {
  lead: { es: 'm', fr: 'm' },
  contact: { es: 'm', fr: 'm' },
  task: { es: 'f', fr: 'f' },
  product: { es: 'm', fr: 'm' },
  student: { es: 'm', fr: 'm' },
};

/**
 * The record type's bare noun for the "nothing importable" title: English
 * keeps the generic plural ("No **leads** can be imported"), Arabic and the
 * Romance languages use the dictionary singular — Arabic's `other` category
 * happens to be exactly that (the classical singular genitive used after
 * 100), and Spanish/French's `one` category is the singular form itself.
 */
function negativeNoun(language: Language, recordType: string): string {
  const forms = nounForms(language, recordType);
  const category: Intl.LDMLPluralRule =
    language === 'es' || language === 'fr' ? 'one' : 'other';
  const template = forms[category] ?? forms.other;
  return template.replace('{n}', '').trim();
}

/**
 * "No leads can be imported" / "لا يمكن استيراد أي عميل محتمل" / "No se
 * puede importar ningún cliente potencial" / "Aucun prospect ne peut être
 * importé" — the import card's title when nothing on it can be imported
 * (ruling 2026-10-13: this always wins over the "of total" phrasing, even
 * when total is 1 or 2, so the count is never stated twice).
 */
function noneImportableLead(language: Language, recordType: string): string {
  const type = recordTypeOf(recordType);
  const noun = negativeNoun(language, recordType);
  switch (language) {
    case 'en':
      return `No ${noun} can be imported`;
    case 'ar':
      return `لا يمكن استيراد أي ${noun}`;
    case 'es': {
      const article = NEGATIVE_GENDER[type].es === 'f' ? 'ninguna' : 'ningún';
      return `No se puede importar ${article} ${noun}`;
    }
    case 'fr': {
      const feminine = NEGATIVE_GENDER[type].fr === 'f';
      const article = feminine ? 'Aucune' : 'Aucun';
      const participle = feminine ? 'importée' : 'importé';
      return `${article} ${noun} ne peut être ${participle}`;
    }
  }
}

/**
 * The import card's title, as localised parts. Three forms, checked in this
 * order: nothing importable (`importable === 0`) always gets the negative
 * `noneImportableLead`, whatever `total` is — the count is never written
 * twice, so this wins even over "of total" at total 1 or 2; every row
 * importable (`importable === total`, both nonzero) gets the same
 * "Import {n} {noun}" phrase as the button (`importButtonLabel`); otherwise
 * "Import {importable} of {total} {noun}", with the noun's plural form
 * agreeing with `total` (the card always shows the total row count,
 * selectable or not). A trailing preposition is appended only when `source`
 * is given and non-empty. `source` itself is returned separately, never
 * interpolated into the sentence: it is a filename (backend text, not
 * localisable) and the caller renders it in its own `dir="auto"` element so
 * a Latin filename reads correctly inside an Arabic title.
 */
export function importTitleParts(
  language: Language,
  recordType: string,
  importable: number,
  total: number,
  source?: string | null
): { lead: string; source: string | null } {
  const lead =
    importable === 0
      ? noneImportableLead(language, recordType)
      : importable === total
        ? importButtonLabel(language, recordType, total)
        : partialImportLead(language, recordType, importable, total);
  const trimmedSource = typeof source === 'string' ? source.trim() : '';
  if (!trimmedSource) return { lead, source: null };
  return { lead: `${lead} ${TITLE_FROM[language]}`, source: trimmedSource };
}

const INVITATIONS: Record<Language, PluralForms> = {
  en: {
    one: '{n} invitation email will be sent (including to students who already have an account)',
    other:
      '{n} invitation emails will be sent (including to students who already have an account)',
  },
  ar: {
    zero: 'لن تُرسَل أي رسالة دعوة عبر البريد الإلكتروني',
    one: 'سيتم إرسال رسالة دعوة واحدة عبر البريد الإلكتروني (بما في ذلك للطلاب الذين لديهم حساب بالفعل)',
    two: 'سيتم إرسال رسالتي دعوة عبر البريد الإلكتروني (بما في ذلك للطلاب الذين لديهم حساب بالفعل)',
    few: 'سيتم إرسال {n} رسائل دعوة عبر البريد الإلكتروني (بما في ذلك للطلاب الذين لديهم حساب بالفعل)',
    many: 'سيتم إرسال {n} رسالة دعوة عبر البريد الإلكتروني (بما في ذلك للطلاب الذين لديهم حساب بالفعل)',
    other:
      'سيتم إرسال {n} رسالة دعوة عبر البريد الإلكتروني (بما في ذلك للطلاب الذين لديهم حساب بالفعل)',
  },
  es: {
    one: 'Se enviará {n} correo electrónico de invitación (incluso a los estudiantes que ya tienen una cuenta)',
    other:
      'Se enviarán {n} correos electrónicos de invitación (incluso a los estudiantes que ya tienen una cuenta)',
  },
  fr: {
    one: "{n} e-mail d'invitation sera envoyé (y compris aux étudiants qui ont déjà un compte)",
    other:
      "{n} e-mails d'invitation seront envoyés (y compris aux étudiants qui ont déjà un compte)",
  },
};

/** Student imports: "4 invitation emails will be sent (…)". */
export function invitationWarning(language: Language, n: number): string {
  return plural(language, n, INVITATIONS[language]);
}

function recordTypeOf(recordType: string): ImportRecordType {
  return (IMPORT_RECORD_TYPES as readonly string[]).includes(recordType)
    ? (recordType as ImportRecordType)
    : 'lead';
}

/**
 * Arabic "{n} more {noun}" for the plan notice: the adjective إضافي agrees
 * with the counted noun — dual, human plural (-ين), non-human plural
 * (feminine singular إضافية) and the accusative singular after 11–99.
 */
const AR_MORE: Record<ImportRecordType, PluralForms> = {
  lead: {
    one: 'عميل محتمل إضافي واحد',
    two: 'عميلين محتملين إضافيين',
    few: '{n} عملاء محتملين إضافيين',
    many: '{n} عميلًا محتملًا إضافيًا',
    other: '{n} عميل محتمل إضافي',
  },
  contact: {
    one: 'جهة اتصال إضافية واحدة',
    two: 'جهتي اتصال إضافيتين',
    few: '{n} جهات اتصال إضافية',
    many: '{n} جهةَ اتصال إضافية',
    other: '{n} جهة اتصال إضافية',
  },
  task: {
    one: 'مهمة إضافية واحدة',
    two: 'مهمتين إضافيتين',
    few: '{n} مهام إضافية',
    many: '{n} مهمةً إضافية',
    other: '{n} مهمة إضافية',
  },
  product: {
    one: 'منتج إضافي واحد',
    two: 'منتجين إضافيين',
    few: '{n} منتجات إضافية',
    many: '{n} منتجًا إضافيًا',
    other: '{n} منتج إضافي',
  },
  student: {
    one: 'طالب إضافي واحد',
    two: 'طالبين إضافيين',
    few: '{n} طلاب إضافيين',
    many: '{n} طالبًا إضافيًا',
    other: '{n} طالب إضافي',
  },
};

/** Arabic definite plural for "no more {noun}" at remaining = 0. */
const AR_PLURAL_DEFINITE: Record<ImportRecordType, string> = {
  lead: 'العملاء المحتملين',
  contact: 'جهات الاتصال',
  task: 'المهام',
  product: 'المنتجات',
  student: 'الطلاب',
};

/** "Your plan allows 5 more leads; the rows over the limit are marked". */
export function planNotice(
  language: Language,
  recordType: string,
  remaining: number
): string {
  const phrase = countNoun(language, recordType, remaining);
  switch (language) {
    case 'en':
      return `Your plan allows ${phrase.replace(
        String(remaining),
        `${remaining} more`
      )}; the rows over the limit are marked`;
    case 'ar':
      if (remaining <= 0) {
        return `لا تسمح خطتك باستيراد المزيد من ${AR_PLURAL_DEFINITE[recordTypeOf(recordType)]}؛ الصفوف التي تتجاوز الحد معلَّمة`;
      }
      return `تسمح خطتك باستيراد ${plural(
        'ar',
        remaining,
        AR_MORE[recordTypeOf(recordType)]
      )} فقط؛ الصفوف التي تتجاوز الحد معلَّمة`;
    case 'es':
      return `Su plan permite ${phrase} más; las filas que superan el límite están marcadas`;
    case 'fr':
      return `Votre forfait permet encore ${phrase} ; les lignes au-delà de la limite sont signalées`;
  }
}

/** "Rows 201–260 are not in this import; ask again for the rest". */
export function truncatedNotice(
  language: Language,
  from: number,
  to: number
): string {
  const range = `${from}–${to}`;
  switch (language) {
    case 'en':
      return `Rows ${range} are not in this import; ask again for the rest`;
    case 'ar':
      return `الصفوف ${range} ليست ضمن هذا الاستيراد؛ اطلب مرة أخرى لاستيراد الباقي`;
    case 'es':
      return `Las filas ${range} no están en esta importación; pídalo de nuevo para el resto`;
    case 'fr':
      return `Les lignes ${range} ne font pas partie de cette importation ; redemandez-les pour le reste`;
  }
}

/** [2, 4, 5, 6, 9] → "2, 4–6, 9". */
export function formatPageList(pages: readonly number[]): string {
  const sorted = Array.from(new Set(pages)).sort((a, b) => a - b);
  const parts: string[] = [];
  let start: number | null = null;
  let prev: number | null = null;
  const flush = () => {
    if (start === null || prev === null) return;
    parts.push(start === prev ? String(start) : `${start}–${prev}`);
  };
  for (const page of sorted) {
    if (prev !== null && page === prev + 1) {
      prev = page;
      continue;
    }
    flush();
    start = page;
    prev = page;
  }
  flush();
  return parts.join(', ');
}

/** "Pages 4–5 could not be read; their rows are not in this import". */
export function unreadableNotice(
  language: Language,
  pages: readonly number[]
): string {
  const list = formatPageList(pages);
  const single = new Set(pages).size === 1;
  switch (language) {
    case 'en':
      return single
        ? `Page ${list} could not be read; its rows are not in this import`
        : `Pages ${list} could not be read; their rows are not in this import`;
    case 'ar':
      return single
        ? `تعذّرت قراءة الصفحة ${list}؛ صفوفها ليست ضمن هذا الاستيراد`
        : `تعذّرت قراءة الصفحات ${list}؛ صفوفها ليست ضمن هذا الاستيراد`;
    case 'es':
      return single
        ? `No se pudo leer la página ${list}; sus filas no están en esta importación`
        : `No se pudieron leer las páginas ${list}; sus filas no están en esta importación`;
    case 'fr':
      return single
        ? `La page ${list} n'a pas pu être lue ; ses lignes ne font pas partie de cette importation`
        : `Les pages ${list} n'ont pas pu être lues ; leurs lignes ne font pas partie de cette importation`;
  }
}

/** A row checkbox's aria-label: "Include row 7, Ahmad Ali". */
export function includeRowLabel(
  language: Language,
  row: number,
  name: string
): string {
  const head = {
    en: `Include row ${row}`,
    ar: `تضمين الصف ${row}`,
    es: `Incluir la fila ${row}`,
    fr: `Inclure la ligne ${row}`,
  }[language];
  if (!name) return head;
  return language === 'ar' ? `${head}، ${name}` : `${head}, ${name}`;
}

/** The duplicate_in_file chip: "Same as row 4". */
export function sameAsRowLabel(language: Language, row: number | string) {
  return {
    en: `Same as row ${row}`,
    ar: `مطابق للصف ${row}`,
    es: `Igual que la fila ${row}`,
    fr: `Identique à la ligne ${row}`,
  }[language];
}

/** The pager: "Page 1 of 3". */
export function pageOfLabel(language: Language, page: number, total: number) {
  return {
    en: `Page ${page} of ${total}`,
    ar: `الصفحة ${page} من ${total}`,
    es: `Página ${page} de ${total}`,
    fr: `Page ${page} sur ${total}`,
  }[language];
}

// ── Problem sentences ────────────────────────────────────────────────────────

export interface ImportProblem {
  code: string;
  field?: string | null;
  detail?: string | null;
}

const PHONE_FIELDS = new Set([
  'phone',
  'phone_number',
  'whatsapp_number',
  'primary_phone_number',
  'secondary_phone_number',
]);
const EMAIL_FIELDS = new Set(['email', 'primary_email', 'secondary_email']);

interface ProblemWords {
  required: (field: string) => string;
  requiredNoField: string;
  notADate: string;
  notANumber: string;
  notAChoice: string;
  notAPhone: string;
  notAnEmail: string;
  invalidField: (field: string) => string;
  invalidNoField: string;
  regionUnknown: string;
  countryAssumed: string;
  nameOnlyDuplicate: string;
  /** email_in_use: the field may be the email OR the phone that collided. */
  inUseByAccount: string;
  notInDocument: (field: string) => string;
  notInDocumentNoField: string;
  /** field_not_allowed: the column was ignored (its value never imported). */
  columnIgnored: (field: string) => string;
  /** field_not_allowed with field "other" (a column outside the fixed set). */
  otherColumnIgnored: string;
  possibleSplitRow: string;
  existingStudentEnrolled: string;
  alreadyInCrm: string;
  alreadyInCrmSame: (field: string) => string;
  sameAsEarlierRow: string;
  overPlanLimit: string;
  needsAttention: string;
  /** unrecognised_choice: a caption-prefixed sentence, the value dropped. */
  unrecognisedChoice: (field: string) => string;
  /** "Label: value" — the language's own colon spacing. */
  withValue: (label: string, value: string) => string;
}

const PROBLEM_WORDS: Record<Language, ProblemWords> = {
  en: {
    required: (f) => `${f} is required`,
    requiredNoField: 'A required value is missing',
    notADate: 'Not a date',
    notANumber: 'Not a number',
    notAChoice: 'Not an allowed choice',
    notAPhone: 'Not a phone number',
    notAnEmail: 'Not an email address',
    invalidField: (f) => `${f} is not valid`,
    invalidNoField: 'Not valid',
    regionUnknown: 'No country code, and no default country is set',
    countryAssumed: 'Country assumed',
    nameOnlyDuplicate: 'Same name as an existing record',
    inUseByAccount: 'Already used by an existing account',
    notInDocument: (f) => `${f} was not found in the document`,
    notInDocumentNoField: 'Not found in the document',
    columnIgnored: (f) => `Column ignored: ${f}`,
    otherColumnIgnored: 'Column ignored: another column',
    possibleSplitRow:
      'May be one record split across pages: check this row and the one next to it',
    existingStudentEnrolled:
      'This student already exists in your organisation; they will be enrolled in this program and sent an invitation',
    alreadyInCrm: 'Already in the CRM',
    alreadyInCrmSame: (f) => `Already in the CRM (same ${f.toLowerCase()})`,
    sameAsEarlierRow: 'Same as an earlier row',
    overPlanLimit: "Over your plan's limit",
    needsAttention: 'Needs attention',
    unrecognisedChoice: (f) =>
      `${f}: value not recognised; this field will be left empty`,
    withValue: (label, value) => `${label}: ${value}`,
  },
  ar: {
    required: (f) => `حقل مطلوب: ${f}`,
    requiredNoField: 'قيمة مطلوبة مفقودة',
    notADate: 'ليس تاريخًا صالحًا',
    notANumber: 'ليس رقمًا صالحًا',
    notAChoice: 'ليس خيارًا مسموحًا',
    notAPhone: 'ليس رقم هاتف صالحًا',
    notAnEmail: 'ليس بريدًا إلكترونيًا صالحًا',
    invalidField: (f) => `قيمة غير صالحة في ${f}`,
    invalidNoField: 'قيمة غير صالحة',
    regionUnknown: 'لا يوجد رمز دولة، ولم تُحدَّد دولة افتراضية',
    countryAssumed: 'تم افتراض الدولة',
    nameOnlyDuplicate: 'يطابق اسم سجل موجود',
    inUseByAccount: 'مستخدم بالفعل في حساب موجود',
    notInDocument: (f) => `لم يُعثر على ${f} في المستند`,
    notInDocumentNoField: 'لم يُعثر عليه في المستند',
    columnIgnored: (f) => `تم تجاهل العمود: ${f}`,
    otherColumnIgnored: 'تم تجاهل عمود آخر',
    possibleSplitRow:
      'قد يكون سجلًا واحدًا مقسّمًا بين صفحتين: تحقق من هذا الصف والصف المجاور له',
    existingStudentEnrolled:
      'هذا الطالب موجود مسبقًا في مؤسستك؛ سيُسجَّل في هذا البرنامج ويُرسَل إليه دعوة',
    alreadyInCrm: 'موجود مسبقًا في النظام',
    alreadyInCrmSame: (f) => `موجود مسبقًا في النظام (تطابق ${f})`,
    sameAsEarlierRow: 'مطابق لصف سابق',
    overPlanLimit: 'يتجاوز حد خطتك',
    needsAttention: 'يحتاج إلى مراجعة',
    unrecognisedChoice: (f) => `${f}: قيمة غير معروفة؛ سيُترك هذا الحقل فارغًا`,
    withValue: (label, value) => `${label}: ${value}`,
  },
  es: {
    required: (f) => `Campo obligatorio: ${f}`,
    requiredNoField: 'Falta un valor obligatorio',
    notADate: 'No es una fecha',
    notANumber: 'No es un número',
    notAChoice: 'No es una opción permitida',
    notAPhone: 'No es un número de teléfono',
    notAnEmail: 'No es una dirección de correo electrónico',
    invalidField: (f) => `Valor no válido en ${f}`,
    invalidNoField: 'Valor no válido',
    regionUnknown: 'Sin código de país, y no hay un país predeterminado',
    countryAssumed: 'País supuesto',
    nameOnlyDuplicate: 'Mismo nombre que un registro existente',
    inUseByAccount: 'Ya lo usa una cuenta existente',
    notInDocument: (f) => `No se encontró en el documento: ${f}`,
    notInDocumentNoField: 'No se encontró en el documento',
    columnIgnored: (f) => `Columna ignorada: ${f}`,
    otherColumnIgnored: 'Columna ignorada: otra columna',
    possibleSplitRow:
      'Puede ser un único registro dividido entre páginas: revise esta fila y la contigua',
    existingStudentEnrolled:
      'Este estudiante ya existe en su organización; se le inscribirá en este programa y se le enviará una invitación',
    alreadyInCrm: 'Ya está en el CRM',
    alreadyInCrmSame: (f) =>
      `Ya está en el CRM (coincidencia en ${f.toLowerCase()})`,
    sameAsEarlierRow: 'Igual que una fila anterior',
    overPlanLimit: 'Supera el límite de su plan',
    needsAttention: 'Requiere revisión',
    unrecognisedChoice: (f) =>
      `${f}: valor no reconocido; este campo se dejará vacío`,
    withValue: (label, value) => `${label}: ${value}`,
  },
  fr: {
    required: (f) => `Champ obligatoire : ${f}`,
    requiredNoField: 'Une valeur obligatoire manque',
    notADate: 'Date non valide',
    notANumber: 'Nombre non valide',
    notAChoice: 'Choix non autorisé',
    notAPhone: 'Numéro de téléphone non valide',
    notAnEmail: 'Adresse e-mail non valide',
    invalidField: (f) => `Valeur non valide pour ${f}`,
    invalidNoField: 'Valeur non valide',
    regionUnknown:
      "Aucun indicatif pays, et aucun pays par défaut n'est défini",
    countryAssumed: 'Pays supposé',
    nameOnlyDuplicate: "Même nom qu'un enregistrement existant",
    inUseByAccount: 'Déjà utilisé par un compte existant',
    notInDocument: (f) => `Introuvable dans le document : ${f}`,
    notInDocumentNoField: 'Introuvable dans le document',
    columnIgnored: (f) => `Colonne ignorée : ${f}`,
    otherColumnIgnored: 'Colonne ignorée : une autre colonne',
    possibleSplitRow:
      'Peut-être un seul enregistrement coupé entre deux pages : vérifiez cette ligne et sa voisine',
    existingStudentEnrolled:
      'Cet étudiant existe déjà dans votre organisation ; il sera inscrit à ce programme et recevra une invitation',
    alreadyInCrm: 'Déjà dans le CRM',
    alreadyInCrmSame: (f) =>
      `Déjà dans le CRM (correspondance : ${f.toLowerCase()})`,
    sameAsEarlierRow: 'Identique à une ligne précédente',
    overPlanLimit: 'Dépasse la limite de votre forfait',
    needsAttention: 'À vérifier',
    unrecognisedChoice: (f) =>
      `${f} : valeur non reconnue ; ce champ restera vide`,
    withValue: (label, value) => `${label} : ${value}`,
  },
};

/**
 * One problem as a sentence in `language`. Covers every binding code
 * (collab/import/problem-codes.md) and the v1 codes it superseded; anything
 * else reads "Needs attention" (+ the field caption).
 */
export function problemSentence(
  problem: ImportProblem,
  language: Language
): string {
  const words = PROBLEM_WORDS[language];
  const field = typeof problem.field === 'string' ? problem.field : '';
  const caption = field ? fieldCaption(language, field) : '';
  const detail =
    typeof problem.detail === 'string' && problem.detail.trim() !== ''
      ? problem.detail
      : '';
  const value = (label: string) =>
    detail ? words.withValue(label, detail) : label;

  switch (problem.code) {
    case 'required':
      return caption ? words.required(caption) : words.requiredNoField;
    case 'email_or_phone_required':
      return words.required(fieldCaption(language, 'email_or_phone'));
    case 'invalid_date':
      return value(words.notADate);
    case 'invalid_number':
      return value(words.notANumber);
    case 'invalid_choice':
      return value(words.notAChoice);
    case 'invalid_phone':
      return value(words.notAPhone);
    case 'invalid_email':
      return value(words.notAnEmail);
    case 'invalid':
      if (PHONE_FIELDS.has(field)) return value(words.notAPhone);
      if (EMAIL_FIELDS.has(field)) return value(words.notAnEmail);
      return value(
        caption ? words.invalidField(caption) : words.invalidNoField
      );
    case 'phone_region_unknown':
      return words.regionUnknown;
    case 'phone_region_assumed':
    case 'phone_country_inferred':
      return value(words.countryAssumed);
    case 'name_only_duplicate':
      return words.nameOnlyDuplicate;
    case 'email_in_use':
      // The field is whichever value collided (email or phone_number), so
      // the sentence names the field rather than saying "email".
      return caption
        ? words.withValue(words.inUseByAccount, caption)
        : words.inUseByAccount;
    case 'not_in_document':
      return caption
        ? words.notInDocument(caption)
        : words.notInDocumentNoField;
    case 'field_not_allowed':
      // A warning (846baca6): the value was dropped, the row still imports.
      if (field === 'other' || !caption) return words.otherColumnIgnored;
      return words.columnIgnored(caption);
    case 'possible_split_row':
      return words.possibleSplitRow;
    case 'existing_student_enrolled':
      return words.existingStudentEnrolled;
    case 'duplicate_existing':
      return caption ? words.alreadyInCrmSame(caption) : words.alreadyInCrm;
    case 'duplicate_in_file':
      return detail ? sameAsRowLabel(language, detail) : words.sameAsEarlierRow;
    case 'over_plan_limit':
      return words.overPlanLimit;
    case 'unrecognised_choice':
      return caption ? words.unrecognisedChoice(caption) : words.needsAttention;
    default:
      return caption
        ? words.withValue(words.needsAttention, caption)
        : words.needsAttention;
  }
}
